import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CalendarIcon,
  ChevronDoubleLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/solid";
import { useGlobalDebounceHandlerDefault } from "@repo/api-services/admin";
import {
  ColumnFiltersState,
  ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { createSearchParams } from "react-router-dom";
import ButtonBase from "../Buttons/ButtonBase";
import SelectorUnstyled from "../DropMenu/SelectorUnstyled";
import DateTimeInput from "../Inputs/DateTimeInput";
import TextInputBase from "../Inputs/TextInputBase";
import TooltipWrapper from "../Tooltip/TooltipWrapper";
import useRouterHandlerHook from "../_hooks/useRouterHandlerHook";
import { PER_PAGE_DATA } from "./UiTable.constant";
import { TableCell, TableRow } from "./components";
import MultipleColumnFilter from "./components/MultipleColumnFilter";
import ToggleColumnDropdown from "./components/ToggleColumnsDropdown";
import { UiTableBaseProps } from "./table.types";

const UiTableV2 = ({
  dataSet,
  tableLabel,
  columnsSchema,
  tableEmptyComponent,
  query,
  setQuery,
  searchPlaceHolder = "Type to search...",
  maxHeightClassName = "max-h-[calc(100vh-340px)]",
  multipleColumnFilterOptions = [],
  disableCalendar = false,
  disableColumnDropDown = false,
  onRefresh,
}: UiTableBaseProps) => {
  //   STATE/s

  const allowedFilters = columnsSchema
    ?.map((item) => item?.column_name)
    .filter(Boolean);

  const filteredMultipleColumnFilterOptions = allowedFilters?.length
    ? multipleColumnFilterOptions?.filter((filter) =>
        allowedFilters?.includes(filter?.column_name)
      )
    : multipleColumnFilterOptions;

  const [columnVisibility, setColumnVisibility] = useState({});
  const [zoomTable, setZoomTable] = useState(false);
  const [filterState, setFilterState] = useState("");
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterColumn, setFilterColumn] = useState(
    filteredMultipleColumnFilterOptions[0]
  );
  const [filterValue, setFilterValue] = useState("");
  // CUSTOM HOOK/s
  const { urlQueries, navigate, navigateTo, navigateBackTo } =
    useRouterHandlerHook();
  const { debouncedQuery } = useGlobalDebounceHandlerDefault(filterValue);
  const formMethods = useForm();
  // VARIABLES
  const tableData = useMemo(
    () => dataSet?.data?.data ?? [],
    [dataSet?.data?.data]
  );
  const tableMetaData = dataSet?.data?.meta || {};
  const dataRefetchLoading = dataSet?.isFetching || false;
  const isTableLoading = dataSet?.isLoading || false;
  const pageSize = dataSet?.data?.meta?.to;

  const table = useReactTable({
    data: tableData,
    columns: columnsSchema,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnVisibility: columnVisibility,
      globalFilter: filterState.trim(),
      columnOrder,
      columnFilters,
    },

    onColumnOrderChange: setColumnOrder,
    onGlobalFilterChange: setFilterState,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
  });
  // HANDLER/s
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/\s+/g, " ");
    value = value.replace(/^\s/, "");
    if (query === undefined) {
      setFilterState(value);
    } else {
      setQuery?.(value);
    }
    navigateTo({
      search: {
        page: "1",
        "t-search": value,
      },
    });
  };
  const tableScrollRef = useRef<HTMLDivElement | null>(null);

  // EFFECT/s
  useEffect(() => {
    const tSearchText = urlQueries?.["t-search"];
    if (tSearchText) {
      if (query === undefined) {
        setFilterState(tSearchText);
      } else {
        setQuery?.(tSearchText);
      }
    }
  }, []);

  // this help us to solve the issue of the table not updating on first render
  useEffect(() => {
    if (isTableLoading || !tableMetaData) {
      return () => {};
    }
    if (tableMetaData?.per_page) {
      table.setPageSize(tableMetaData?.per_page);
    }
  }, [dataSet?.data?.meta, dataSet?.isLoading]);

  useEffect(() => {
    if (!filterColumn?.code || !debouncedQuery || !filterValue) {
      return;
    }
    navigateTo({
      deleteSearchKey: filteredMultipleColumnFilterOptions?.map(
        (item) => item.code
      ),
      search: {
        [filterColumn?.code]: debouncedQuery,
      },
    });
  }, [filterColumn?.code, debouncedQuery, filterValue]);

  useEffect(() => {
    if (pageSize) {
      const obj = PER_PAGE_DATA.find((items) => items.value === pageSize);
      if (obj) {
        formMethods.setValue("per_page", obj, { shouldDirty: false });
        table.setPageSize(obj.value);
      }
    }
  }, [pageSize, formMethods, table]);
  useEffect(() => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth", // optional
      });
    }
  }, [tableMetaData?.current_page]);

  return (
    <div
      className={`flex flex-col gap-2 border-none bg-white  transition-all duration-300 ${
        zoomTable
          ? `fixed py-5 px-5 z-50 top-[2%] left-[2%] w-[96vw] 
              h-[95vh] rounded-xl border border-lightGray  shadow-2xl`
          : " "
      }`}
    >
      {tableLabel && zoomTable && (
        <h1 className="text-md font-semibold ">{tableLabel}</h1>
      )}
      <section className="flex items-center justify-between">
        <section>
          <div
            data-show={tableMetaData?.total > 10}
            className="flex items-center border-2 border-extraLightGray h-9 rounded-lg pl-2 data-[show=false]:hidden "
          >
            <p className="text-sm font-semibold text-body pr-2 select-none">
              Entries
            </p>
            <div className="w-16">
              <Controller
                name="per_page"
                control={formMethods.control}
                defaultValue={{ name: 15, id: "2" }}
                render={({ field: { value, onChange } }) => (
                  <SelectorUnstyled
                    value={value}
                    onChange={(selectedPageSize) => {
                      onChange(selectedPageSize);
                      navigateTo({
                        search: {
                          pagesize: selectedPageSize?.value,
                        },
                        replace: true,
                      });
                    }}
                    options={PER_PAGE_DATA}
                    error={formMethods.formState.errors}
                    placeholder="Per Page"
                    className="border-l-2 border-extraLightGray text-body h-9"
                  />
                )}
              />
            </div>
          </div>
        </section>
        {/* <Selector /> */}
        <div className="flex items-center self-end gap-2">
          {filteredMultipleColumnFilterOptions?.length ? (
            <MultipleColumnFilter
              filterColumn={filterColumn}
              setFilterColumn={setFilterColumn}
              filterValue={filterValue}
              setFilterValue={setFilterValue}
              multipleColumnFilterOptions={filteredMultipleColumnFilterOptions}
            />
          ) : (
            <div className="w-56">
              <TextInputBase
                type="text"
                value={query || filterState}
                onChange={handleChange}
                placeholder={searchPlaceHolder}
                className="h-9 pl-3 pr-10 text-sm text-body "
                icon={
                  <>
                    {query || filterState ? (
                      <button
                        type="button"
                        className="flex items-center rounded-md justify-center bg-primary/10 text-primary p-0.5"
                        onClick={() => {
                          setFilterState("");
                          setQuery?.("");
                          navigateBackTo({
                            searchKey: `t-search`,
                          });
                        }}
                      >
                        <XMarkIcon className=" size-4" />
                      </button>
                    ) : (
                      <MagnifyingGlassIcon className="size-4" />
                    )}
                  </>
                }
              />
            </div>
          )}

          <DateTimeInput
            icon={<CalendarIcon className="size-5" />}
            className={`text-body size-7 mt-1 cursor-pointer outline-none ${
              disableCalendar ? "hidden" : ""
            }`}
          />
          <ButtonBase
            icon={
              <ArrowPathIcon
                className={`size-5 ${dataRefetchLoading ? "animate-spin" : ""}`}
              />
            }
            className="size-7 text-body "
            buttonType="tertiary"
            onClick={() => {
              dataSet.refetch?.();
              onRefresh?.();
            }}
          />

          <ToggleColumnDropdown
            icon={<AdjustmentsHorizontalIcon className="size-5 " />}
            headText="Customize displayed columns"
            options={table.getAllLeafColumns()}
            className={`text-body size-7 cursor-pointer ${
              disableColumnDropDown ? "hidden" : ""
            }`}
            setColumnOrder={table.setColumnOrder}
          />
          <div className="relative">
            <TooltipWrapper
              className="top-1/2 right-full -translate-y-1/2"
              name={!zoomTable ? "Zoom Table" : "Minimize Table"}
            >
              <ButtonBase
                icon={
                  !zoomTable ? (
                    <ArrowsPointingOutIcon className="size-5 " />
                  ) : (
                    <ArrowsPointingInIcon className="size-5 " />
                  )
                }
                buttonType="tertiary"
                className="text-body size-7 "
                onClick={() => {
                  setZoomTable(!zoomTable);
                }}
              />
            </TooltipWrapper>
          </div>
        </div>
      </section>
      {!isTableLoading ? (
        <div
          ref={tableScrollRef}
          className={`flex flex-col w-full    ${
            !zoomTable
              ? `overflow-y-auto ${maxHeightClassName}`
              : "overflow-y-auto max-h-[calc(100vh-195px)]"
          }`}
        >
          <table className="w-full rounded">
            <thead className="h-10 text-nowrap">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow type="head" key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableCell type="th" key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </thead>
            {table.getRowModel().rows.length === 0 ? (
              <tbody>
                <tr className="text-body ">
                  <td
                    colSpan={table.getHeaderGroups()[0].headers.length}
                    className="h-10 rounded-md"
                  >
                    <div className="flex items-center justify-center h-full">
                      {tableEmptyComponent || (
                        <p className="font-medium text-sm">
                          No data available to show!
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell type="td" key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </tbody>
            )}
          </table>
        </div>
      ) : (
        <div className="h-56 w-full flex items-center justify-center bg-offWhite rounded-lg">
          <span className="h-6 w-6 border-2 border-gray rounded-full border-b-transparent animate-spin" />
        </div>
      )}
      {tableMetaData?.per_page ? (
        <div className="flex items-center justify-between pt-2 border-t border-extraLightGray  ">
          <section className="flex item-start gap-2">
            <p className="text-sm text-body/80  font-semibold flex items-center gap-1">
              <span>Showing</span>
              <span className="font-extrabold text-subHeading ">
                {tableMetaData?.to}
              </span>
              <span> of {tableMetaData?.total} entries</span>
            </p>
          </section>
          <section className="flex items-center justify-end gap-3 ">
            {!!tableData.length && (
              <span className="flex items-center gap-1 text-sm">
                <span className="font-medium"> Page </span>
                <strong>{tableMetaData?.current_page} </strong>
                <span className="font-medium"> of </span>
                <strong> {tableMetaData?.last_page}</strong>
              </span>
            )}
            <>
              <ButtonBase
                text="First"
                buttonType="tertiary"
                className="h-7 flex-row-reverse font-semibold"
                icon={<ChevronDoubleLeftIcon className="size-4" />}
                disabled={tableMetaData?.current_page === 1}
                onClick={() =>
                  navigate({
                    search: createSearchParams({
                      ...urlQueries,
                      page: "1",
                    }).toString(),
                  })
                }
              />
              <ButtonBase
                disabled={tableMetaData?.current_page === 1}
                onClick={() =>
                  navigate({
                    search: createSearchParams({
                      ...urlQueries,
                      page: String(tableMetaData?.current_page - 1),
                    }).toString(),
                  })
                }
                text="Prev"
                buttonType="tertiary"
                className="h-7 flex-row-reverse font-semibold"
                icon={<ChevronLeftIcon className="size-4" />}
              />
              <ButtonBase
                disabled={
                  tableMetaData?.current_page === tableMetaData?.last_page
                }
                icon={<ChevronRightIcon className="size-4" />}
                onClick={() =>
                  navigate({
                    search: createSearchParams({
                      ...urlQueries,
                      page: tableMetaData?.current_page + 1,
                    }).toString(),
                  })
                }
                text="Next"
                buttonType="tertiary"
                className=" h-7 font-semibold"
              />
              <ButtonBase
                disabled={
                  tableMetaData?.current_page === tableMetaData?.last_page
                }
                icon={<ChevronDoubleRightIcon className="size-4" />}
                onClick={() =>
                  navigate({
                    search: createSearchParams({
                      ...urlQueries,
                      page: tableMetaData?.last_page,
                    }).toString(),
                  })
                }
                text="Last"
                buttonType="tertiary"
                className=" h-7 font-semibold"
              />
            </>
          </section>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default UiTableV2;
